import React from 'react';
import * as THREE from 'three';

interface MgRibbonSpoolProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  /** How full the spool is, 0..1 — affects the visible coil thickness. */
  fillAmount?: number;
}

/**
 * Magnesium ribbon spool — a small plastic reel holding a long roll of
 * Mg ribbon. Visible on the polishing workbench to give context for
 * where the ribbon is cut from.
 */
export function MgRibbonSpool({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  fillAmount = 0.85,
}: MgRibbonSpoolProps) {
  const reelRadius = 0.045;
  const reelInner = 0.015;
  const reelThickness = 0.032;
  const ribbonOuter = reelInner + (reelRadius - reelInner) * Math.max(0.1, fillAmount);

  return (
    <group position={position} rotation={rotation} scale={scale}>
      {/* Inner core */}
      <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
        <cylinderGeometry args={[reelInner, reelInner, reelThickness * 1.15, 18]} />
        <meshStandardMaterial color="#222226" roughness={0.7} metalness={0.1} />
      </mesh>

      {/* Coiled ribbon (silver) */}
      <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
        <cylinderGeometry args={[ribbonOuter, ribbonOuter, reelThickness, 32, 1, true]} />
        <meshStandardMaterial color="#cfd6dc" metalness={0.85} roughness={0.32} side={THREE.DoubleSide} />
      </mesh>

      {/* Side disc 1 (semi-transparent reel cheek) */}
      <mesh position={[0, 0, reelThickness / 2]} rotation={[0, 0, 0]}>
        <cylinderGeometry args={[reelRadius, reelRadius, 0.003, 36]} />
        <meshStandardMaterial color="#2a2a30" roughness={0.55} metalness={0.15} transparent opacity={0.92} />
      </mesh>
      {/* Side disc 2 */}
      <mesh position={[0, 0, -reelThickness / 2]}>
        <cylinderGeometry args={[reelRadius, reelRadius, 0.003, 36]} />
        <meshStandardMaterial color="#2a2a30" roughness={0.55} metalness={0.15} transparent opacity={0.92} />
      </mesh>

      {/* Loose tail of ribbon hanging off the reel for visual interest */}
      <mesh position={[reelRadius + 0.018, -0.008, 0]} rotation={[0, 0, -0.4]} castShadow>
        <boxGeometry args={[0.05, 0.0008, 0.006]} />
        <meshStandardMaterial color="#cfd6dc" metalness={0.85} roughness={0.32} />
      </mesh>

      {/* Label sticker */}
      <mesh position={[0, 0, reelThickness / 2 + 0.003]}>
        <cylinderGeometry args={[reelRadius * 0.6, reelRadius * 0.6, 0.001, 24]} />
        <meshStandardMaterial color="#f5f0e6" roughness={0.9} />
      </mesh>
    </group>
  );
}

export default MgRibbonSpool;
